import React, { useMemo } from 'react';
import { Link } from 'react-router';
import { Box, Typography } from '@mui/material';
import { describeYards, summarizeYards, SummaryYard } from '../../seo/marketSummary';
import { marketPath, nearestMarkets } from '../../seo/markets.mjs';
import type { Market } from '../../seo/markets.mjs';

interface MarketSummaryProps {
  market: Market;
  /** The yards the list shows, so the numbers always match it. */
  yards: SummaryYard[];
}

/**
 * Market page text under the yard list (securspace-ui#3056): what the list adds up to,
 * and the nearest other markets. Below the list so phones still open on the first yard;
 * rendered in the page (not behind a click) so the prerender snapshot carries it.
 */
const MarketSummary: React.FC<MarketSummaryProps> = ({ market, yards }) => {
  const text = useMemo(() => describeYards(market, summarizeYards(yards)), [market, yards]);
  const nearby = useMemo(() => nearestMarkets(market), [market]);
  if (!text) return null;
  return (
    <Box component="section" aria-label={`Truck parking and storage in ${market.city}, ${market.state}`} sx={{ px: 2, py: 2.5 }}>
      <Typography variant="body2" color="text.secondary">{text}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        Nearby markets:{' '}
        {nearby.map((m, i) => (
          <React.Fragment key={marketPath(m)}>
            {i > 0 && ' · '}
            <Link to={marketPath(m)}>{m.city}, {m.state}</Link>
          </React.Fragment>
        ))}
      </Typography>
    </Box>
  );
};

export default MarketSummary;
